import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Skeleton,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CreateNewFolder as CreateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DriveFileMove as MoveIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import fileCabinetService from 'services/fileCabinet';

const ActivityLogModal = ({ 
  open, 
  onClose, 
  clientID 
}) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && clientID) {
      loadActivities();
    }
  }, [open, clientID]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const activityLog = await fileCabinetService.getActivityLog(clientID, 50);
      console.log('Loaded activities:', activityLog);
      setActivities(activityLog);
    } catch (error) {
      console.error('Error loading activity log:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'ADD_TO_FOLDER':
        return <AddIcon fontSize="small" color="success" />;
      case 'REMOVE_FROM_FOLDER':
        return <RemoveIcon fontSize="small" color="error" />;
      case 'CREATE_FOLDER':
        return <CreateIcon fontSize="small" color="primary" />;
      case 'DELETE_FOLDER':
        return <DeleteIcon fontSize="small" color="error" />;
      case 'RENAME_FOLDER':
        return <EditIcon fontSize="small" color="info" />;
      case 'MOVE_FOLDER':
        return <MoveIcon fontSize="small" color="warning" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'ADD_TO_FOLDER':
        return 'Added to Folder';
      case 'REMOVE_FROM_FOLDER':
        return 'Removed from Folder';
      case 'CREATE_FOLDER':
        return 'Created Folder';
      case 'DELETE_FOLDER':
        return 'Deleted Folder';
      case 'RENAME_FOLDER':
        return 'Renamed Folder';
      case 'MOVE_FOLDER':
        return 'Moved Folder';
      default:
        return action;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'ADD_TO_FOLDER':
      case 'CREATE_FOLDER':
        return 'success';
      case 'REMOVE_FROM_FOLDER':
      case 'DELETE_FOLDER':
        return 'error';
      case 'RENAME_FOLDER':
        return 'info';
      case 'MOVE_FOLDER':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatActivityDescription = (activity) => {
    const { action, itemName, itemType } = activity;
    
    switch (action) {
      case 'ADD_TO_FOLDER':
        return `Added ${itemType} "${itemName}" to folder`;
      case 'REMOVE_FROM_FOLDER':
        return `Removed ${itemType} "${itemName}" from folder`;
      case 'CREATE_FOLDER':
        return `Created folder "${itemName}"`;
      case 'DELETE_FOLDER':
        return `Deleted folder "${itemName}"`;
      case 'RENAME_FOLDER':
        return `Renamed folder to "${itemName}"`;
      case 'MOVE_FOLDER':
        return `Moved folder "${itemName}"`;
      default:
        return `${action} - ${itemName || 'Unknown'}`;
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              File Cabinet Activity Log
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            File Cabinet Activity Log
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {activities.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No activity yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              File cabinet activities will appear here
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity, index) => (
                  <TableRow key={activity.id || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getActionIcon(activity.action)}
                        <Chip
                          label={getActionLabel(activity.action)}
                          size="small"
                          color={getActionColor(activity.action)}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatActivityDescription(activity)}
                      </Typography>
                      {activity.details && (
                        <Typography variant="caption" color="text.secondary">
                          {activity.details}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2">
                          {activity.performedByUser?.firstName || 'Unknown'} {activity.performedByUser?.lastName || 'User'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {activity.createdAt ? format(new Date(activity.createdAt), 'MMM dd, yyyy') : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.createdAt ? format(new Date(activity.createdAt), 'h:mm a') : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={loadActivities} variant="outlined">
          Refresh
        </Button>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityLogModal;